import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

type NotificationType = 'message' | 'order' | 'price_drop';

export interface Notification {
  id: string;
  title: string;
  description: string;
  time: string;
  type: NotificationType;
  data?: {
    chatId?: string;
    orderId?: string;
    productId?: string;
  };
}

interface NotificationsContextType {
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  unreadCount: number;
  refreshCount: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  addNotification: () => {},
  removeNotification: () => {},
  clearNotifications: () => {},
  unreadCount: 0,
  refreshCount: async () => {}
});

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'New Message',
      description: 'You have a new message from a seller',
      time: '2 hours ago',
      type: 'message',
      data: { chatId: '123' }
    },
    {
      id: '2',
      title: 'Order Update',
      description: 'Your order has been shipped',
      time: '1 day ago',
      type: 'order',
      data: { orderId: '456' }
    },
    {
      id: '3',
      title: 'Price Drop',
      description: 'A product in your wishlist is now on sale',
      time: '2 days ago',
      type: 'price_drop',
      data: { productId: '789' }
    }
  ]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  const fetchNotificationCount = async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      // Get all unread notifications
      const { data, error } = await supabase
        .from('notifications')
        .select('id')
        .eq('recipient_id', user.id)
        .eq('status', 'unread');

      if (error) throw error;

      setUnreadCount(data?.length || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  useEffect(() => {
    fetchNotificationCount();

    // Subscribe to notifications table changes
    const channel = supabase
      .channel('notifications_count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: user ? `recipient_id=eq.${user.id}` : undefined,
        },
        () => {
          fetchNotificationCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const refreshCount = async () => {
    await fetchNotificationCount();
  };

  const addNotification = (notification: Notification) => {
    setNotifications(prev => [notification, ...prev]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
        unreadCount,
        refreshCount
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext); 